import { ExpandablePanel } from '@dealstack/frontend/shared/components/ExpandablePanel/ExpandablePanel';
import { NumberInput } from '@dealstack/frontend/shared/components/NumberInput/NumberInput';
import { Controller } from 'react-hook-form';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { IconDeviceFloppy, IconX } from '@tabler/icons-react';
import { DropdownInput } from '@dealstack/frontend/shared/components/DropdownInput/DropdownInput';
import { useDropdownInput } from '@dealstack/frontend/shared/components/DropdownInput/useDropdownInput';
import { useEffect } from 'react';
import {
	CreateVestingTermLeaverProvisionDto,
	UpdateVestingTermLeaverProvisionDto,
	VestingTermLeaverProvisionRes,
	VestingTermLeaverProvisionType,
	VestingTermPurchaseTermsType,
} from '@dealstack/shared/lib/generated';
import { useLeaverProvisionController } from '@dealstack/frontend/shared/components/VestingTerms/LeaverProvision/useLeaverProvisionController';

export const LeaverProvision = ({
	vestingTermId,
	setLeaverProvisionCreatorOpen,
	onCreateVestingTermLeaverProvision,
	onUpdateVestingTermLeaverProvision,
	onDeleteVestingTermLeaverProvision,
	leaverProvisionCreatorOpen,
	leaverProvision,
}: {
	vestingTermId: string;
	setLeaverProvisionCreatorOpen: (bool: boolean) => void;
	onCreateVestingTermLeaverProvision: (
		vestingTermLeaverProvision: CreateVestingTermLeaverProvisionDto
	) => void;
	onUpdateVestingTermLeaverProvision: (
		leaverProvisionId: string,
		formValues: UpdateVestingTermLeaverProvisionDto
	) => void;
	onDeleteVestingTermLeaverProvision: (leaverProvisionId: string) => void;
	leaverProvisionCreatorOpen?: boolean;
	leaverProvision?: VestingTermLeaverProvisionRes;
}) => {
	const tCommonUi = useTranslationCommonUi();

	const { toggleExpanded, handleSubmit, control, isExpanded, onSubmit, setValue, errors } =
		useLeaverProvisionController(
			vestingTermId,
			setLeaverProvisionCreatorOpen,
			onCreateVestingTermLeaverProvision,
			onUpdateVestingTermLeaverProvision,
			leaverProvision
		);

	const leaverTypeOptions = [
		{
			label: tCommonUi('vesting_terms.good_leaver'),
			value: VestingTermLeaverProvisionType.GOOD_LEAVER,
		},
		{
			label: tCommonUi('vesting_terms.intermediate_leaver'),
			value: VestingTermLeaverProvisionType.INTERMEDIATE_LEAVER,
		},
		{
			label: tCommonUi('vesting_terms.bad_leaver'),
			value: VestingTermLeaverProvisionType.BAD_LEAVER,
		},
	];

	const purchaseOptions = [
		{
			label: tCommonUi('vesting_terms.purchase_price'),
			value: VestingTermPurchaseTermsType.PURCHASE_PRICE,
		},
		{
			label: tCommonUi('vesting_terms.fair_market'),
			value: VestingTermPurchaseTermsType.FAIR_MARKET,
		},
		{
			label: tCommonUi('vesting_terms.lower_price'),
			value: VestingTermPurchaseTermsType.LOWER_PRICE,
		},
		{
			label: tCommonUi('vesting_terms.higher_price'),
			value: VestingTermPurchaseTermsType.HIGHER_PRICE,
		},
		{
			label: tCommonUi('vesting_terms.purchase_price_discount'),
			value: VestingTermPurchaseTermsType.PURCHASE_PRICE_DISCOUNT,
		},
		{
			label: tCommonUi('vesting_terms.fair_market_discount'),
			value: VestingTermPurchaseTermsType.FAIR_MARKET_DISCOUNT,
		},
	];

	const { value: leaverTypeValue, onChange: onLeaverTypeChange } = useDropdownInput({
		initialValue: leaverProvision?.type ?? leaverTypeOptions[0].value,
		options: leaverTypeOptions,
	});

	const { value: vestedPurchaseTermsValue, onChange: onVestedPurchaseTermsChange } =
		useDropdownInput({
			initialValue: leaverProvision?.vestedPurchaseTerms ?? purchaseOptions[0].value,
			options: leaverTypeOptions,
		});

	const { value: unvestedPurchaseTermsValue, onChange: onUnvestedPurchaseTermsChange } =
		useDropdownInput({
			initialValue: leaverProvision?.vestedPurchaseTerms ?? purchaseOptions[0].value,
			options: leaverTypeOptions,
		});

	useEffect(() => {
		setValue('type', leaverTypeValue as VestingTermLeaverProvisionRes['type']);
	}, [leaverTypeValue, setValue]);

	useEffect(() => {
		setValue(
			'vestedPurchaseTerms',
			vestedPurchaseTermsValue as VestingTermLeaverProvisionRes['vestedPurchaseTerms']
		);
	}, [vestedPurchaseTermsValue, setValue]);

	useEffect(() => {
		setValue(
			'unvestedPurchaseTerms',
			unvestedPurchaseTermsValue as VestingTermLeaverProvisionRes['unvestedPurchaseTerms']
		);
	}, [unvestedPurchaseTermsValue, setValue]);

	const leaverTitle =
		leaverTypeOptions.find((leaverTypeOption) => leaverTypeOption.value === leaverTypeValue)
			?.label ?? '';

	return (
		<ExpandablePanel
			title={leaverTitle}
			isExpanded={leaverProvisionCreatorOpen || isExpanded}
			onExpandClick={() => toggleExpanded()}
			cardActions={
				leaverProvision
					? [
							{
								title: tCommonUi('vesting_terms.make_changes'),
								onClick: toggleExpanded,
								intent: 'normal',
							},
							{
								title: tCommonUi('vesting_terms.remove'),
								onClick: () => {
									onDeleteVestingTermLeaverProvision(leaverProvision.id);
								},
								intent: 'danger',
							},
						]
					: []
			}
			panelSummaryItems={[
				tCommonUi('vesting_terms.leaver_provision.vested_share', {
					vestedSharesPercentage: leaverProvision?.vestedSharesPercentage,
				}) +
					tCommonUi(
						`vesting_terms.leaver_provision.${leaverProvision?.vestedPurchaseTerms.toLowerCase()}`
					),
				tCommonUi('vesting_terms.leaver_provision.unvested_share', {
					unvestedSharesPercentage: leaverProvision?.unvestedSharesPercentage,
				}) +
					tCommonUi(
						`vesting_terms.leaver_provision.${leaverProvision?.unvestedPurchaseTerms.toLowerCase()}`
					),
			]}
		>
			<form className="p-4" onSubmit={handleSubmit(onSubmit)}>
				<div className="w-full">
					<div>
						<div className="font-semibold">
							{tCommonUi('vesting_terms.leaver_type')}
						</div>
						<DropdownInput
							value={leaverTypeValue}
							options={leaverTypeOptions}
							onChange={onLeaverTypeChange}
						/>
						<div className="mt-2">
							{tCommonUi('vesting_terms.leaver_provision_title_leaving_terms')}
						</div>
					</div>

					<div className="border border-gray-300 border-solid rounded-lg mt-4">
						<div className="p-2">
							<div className="font-semibold">
								{tCommonUi('vesting_terms.leaver_provision_vested_shares')}
							</div>
							<div className="flex mt-2">
								<div className="w-1/2">
									<div className="font-semibold">
										{tCommonUi('vesting_terms.leaver_provision_repurchaseable')}
									</div>

									<Controller
										name="vestedSharesPercentage"
										control={control}
										render={({ field }) => (
											<NumberInput
												inputPlaceholder=""
												{...field}
												errorMessage={
													errors.vestedSharesPercentage?.message &&
													errors.vestedSharesPercentage.message.toString()
												}
											/>
										)}
									/>
								</div>
								<div className="w-1/2 ml-4">
									<div className="font-semibold">
										{tCommonUi('vesting_terms.leaver_provision_purchase_terms')}
									</div>

									<DropdownInput
										value={vestedPurchaseTermsValue}
										options={purchaseOptions}
										onChange={onVestedPurchaseTermsChange}
									/>
								</div>
							</div>
						</div>
						<div className="p-2 border-t border-gray-300 border-solid">
							<div className="font-semibold">
								{tCommonUi('vesting_terms.leaver_provision_unvested_shares')}
							</div>
							<div className="flex mt-2">
								<div className="w-1/2">
									<div className="font-semibold">
										{tCommonUi('vesting_terms.leaver_provision_repurchaseable')}
									</div>

									<Controller
										name="unvestedSharesPercentage"
										control={control}
										render={({ field }) => (
											<NumberInput
												inputPlaceholder=""
												{...field}
												errorMessage={
													errors.unvestedSharesPercentage?.message &&
													errors.unvestedSharesPercentage.message.toString()
												}
											/>
										)}
									/>
								</div>
								<div className="w-1/2 ml-4">
									<div className="font-semibold">
										{tCommonUi('vesting_terms.leaver_provision_purchase_terms')}
									</div>

									<DropdownInput
										value={unvestedPurchaseTermsValue}
										options={purchaseOptions}
										onChange={onUnvestedPurchaseTermsChange}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="flex justify-between mt-4">
					<button
						className="bg-white border-0 text-red-500 hover:text-red-700 flex items-center justify-end font-bold text-sm"
						onClick={() =>
							leaverProvisionCreatorOpen
								? setLeaverProvisionCreatorOpen(false)
								: toggleExpanded()
						}
					>
						<IconX stroke={2} size={24} className="mr-1" />
						{tCommonUi('vesting_terms.cancel')}
					</button>

					<button
						className="bg-blue-600 hover:bg-blue-700 flex items-center text-white px-2 py-2 font-semibold rounded border-none"
						type="submit"
					>
						<IconDeviceFloppy stroke={2} size={24} className="mr-1" />
						{tCommonUi('vesting_terms.save')}
					</button>
				</div>
			</form>
		</ExpandablePanel>
	);
};
